import { useState } from 'react'
import { Link } from 'react-router-dom'
import { formatModified } from './format'

// Glyph paths from the v3 export (btn_6/arrow-back-green_2, btn_10/Frame_3), translated to their own
// origin. Only the glyphs were exported, not the icon frames (spec 6.11).
function BackArrow() {
  return (
    <svg width="11" height="8" viewBox="0 0 11 8" fill="none" aria-hidden="true" className="shrink-0">
      <path d="M0.67 3.997H10.67M4.67 7.331L0.67 3.997L4.67 0.664" stroke="#4AA52A" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

// `hoverColor` repaints both strokes, as Add Scenario's hover repaints its whole icon (letter 99, 5.1).
function TemplatesIcon({ hoverColor = null }) {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" className="shrink-0">
      <path
        d="M7.19 17.03H3.11C2.33 17.03 1.7 16.4 1.7 15.62V4.38C1.7 3.6 2.33 2.97 3.11 2.97H7.19C7.96 2.97 8.59 3.6 8.59 4.38V15.62C8.59 16.4 7.96 17.03 7.19 17.03ZM16.89 8.59H12.81C12.04 8.59 11.41 7.96 11.41 7.19V4.38C11.41 3.6 12.04 2.97 12.81 2.97H16.89C17.67 2.97 18.3 3.6 18.3 4.38V7.19C18.3 7.96 17.67 8.59 16.89 8.59Z"
        stroke={hoverColor ?? '#17270D'}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12.81 17.03H16.89C17.67 17.03 18.3 16.4 18.3 15.62V12.81C18.3 12.04 17.67 11.41 16.89 11.41H12.81C12.04 11.41 11.41 12.04 11.41 12.81V15.62C11.41 16.4 12.04 17.03 12.81 17.03Z"
        stroke={hoverColor ?? '#4AA52A'}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// Spec 4.1. Line heights are chosen so the baselines land where the 1440 frame draws them below the
// navbar (37.55 / 67 / 99.6 / 123.35) under the page's own 24px top padding. Letter 99 (6.1): the
// address is 20/24 like ARV Analysis; Last Modified, Templates, the ribbon and the strip move down 4.
// `ribbon`: from lg it sits under Templates, right-aligned beside the address (frame y 173-223, 16
// below the button) and capped at the right card column's width, (header - 24) / 2 up to 487, read
// through the grid's own inline-size container; below lg it takes its own row.
export default function FlipPageHeader({ propertyId, address, modified, onOpenTemplates, ribbon = null }) {
  const modifiedText = formatModified(modified)
  // Same hover as Add Scenario (ScenarioRibbon.jsx): border, icon and label all go #328C12.
  const [templatesHovered, setTemplatesHovered] = useState(false)

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-[16px] items-start [container-type:inline-size]">
      <div className="min-w-0 lg:row-span-2">
        <Link
          to={`/property/${propertyId}`}
          className="flex w-fit items-center gap-x-[4px] text-[13px] leading-[18px] font-semibold text-[#4AA52A] hover:opacity-70 transition-opacity outline-none focus-visible:ring-2 focus-visible:ring-brand-green rounded-[2px]"
        >
          <BackArrow />
          <span>Back to Property Dashboard</span>
        </Link>
        <h1 className="mt-[6px] text-[20px] leading-[24px] font-extrabold text-brand-dark">Flip Analysis</h1>
        {address ? <p className="mt-[12px] text-[20px] leading-[24px] font-light text-brand-dark break-words">{address}</p> : null}
        {modifiedText ? (
          <p className="mt-[8px] text-[11px] leading-[15px] font-normal text-brand-secondary">Last Modified: {modifiedText}</p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onOpenTemplates}
        onMouseEnter={() => setTemplatesHovered(true)}
        onMouseLeave={() => setTemplatesHovered(false)}
        className={`mt-[7px] justify-self-end w-[125px] h-[40px] shrink-0 inline-flex items-center pl-[18px] gap-x-[5px] rounded-[20px] bg-white border text-[13px] font-semibold outline-none focus-visible:ring-2 focus-visible:ring-brand-green ${
          templatesHovered ? 'border-brand-hover text-brand-hover' : 'border-brand-fieldBdr text-brand-dark'
        }`}
      >
        <TemplatesIcon hoverColor={templatesHovered ? '#328C12' : null} />
        <span>Templates</span>
      </button>
      {ribbon ? (
        <div className="col-span-2 mt-[24px] min-w-0 lg:col-span-1 lg:col-start-2 lg:mt-[16px] lg:max-w-[min(487px,calc((100cqw_-_24px)_/_2))]">
          {ribbon}
        </div>
      ) : null}
    </div>
  )
}
